import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Table } from "@/shared/ui";
import { formatDateForDisplay } from "@/shared/utils";
import { type GetApplicationsResponse, type SortConfig } from "../../../types";
import { STATUS_MAP } from "../../../consts";
import { getApplicationDisplayStatus } from "../../../utils";
import { type UseHiringActionsReturn, type UseHiringMutationsReturn } from "../../../hooks";
import ApplicationActionsDropdown from "./ApplicationActionsDropdown";

interface Props {
  applications: GetApplicationsResponse[];
  sortConfig: SortConfig | null;
  onSort: (key: SortConfig["key"]) => void;
  onOpenApplication: (id: number) => void;
  actions: UseHiringActionsReturn;
  mutations: UseHiringMutationsReturn;
  downloads: {
    onDownloadProfile: (id: number) => void;
    isDownloading: boolean;
  };
}

export default function HiringTableView({
  applications,
  sortConfig,
  onSort,
  onOpenApplication,
  actions,
  mutations,
  downloads,
}: Props) {
  const { t } = useTranslation("HiringPage");
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);

  return (
    <div className="overflow-x-auto page-scroll pb-2 min-w-0">
      <Table.Table>
        <Table.Header>
          <tr>
            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "candidate_name" ? sortConfig.direction : undefined}
              onSort={() => onSort("candidate_name")}>
              {t("table.candidate")}
            </Table.HeadCell>

            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "invitation_email" ? sortConfig.direction : undefined}
              onSort={() => onSort("invitation_email")}>
              {t("table.email")}
            </Table.HeadCell>

            <Table.HeadCell>{t("table.phone")}</Table.HeadCell>

            <Table.HeadCell>{t("table.position")}</Table.HeadCell>

            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "invitation_date" ? sortConfig.direction : undefined}
              onSort={() => onSort("invitation_date")}>
              {t("table.invitationDate")}
            </Table.HeadCell>

            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "status" ? sortConfig.direction : undefined}
              onSort={() => onSort("status")}>
              {t("table.status")}
            </Table.HeadCell>

            <Table.HeadCell>{t("table.actions")}</Table.HeadCell>
          </tr>
        </Table.Header>

        <Table.Body>
          {applications.map((app, index) => {
            return (
              <Table.Row key={app.id} onClick={() => onOpenApplication(app.id)} className="cursor-pointer">
                <Table.Cell isBold>{app.candidate_name}</Table.Cell>

                <Table.Cell>{app.invitation_email}</Table.Cell>

                <Table.Cell>{app.phone || "—"}</Table.Cell>

                <Table.Cell>{app.job_position || "—"}</Table.Cell>

                <Table.Cell>{formatDateForDisplay(app.invitation_date, false) || "—"}</Table.Cell>

                <Table.Cell>
                  <Badge
                    variant="soft"
                    color={STATUS_MAP[getApplicationDisplayStatus(app)].color}
                    text={t(STATUS_MAP[getApplicationDisplayStatus(app)].label)}
                    icon={STATUS_MAP[getApplicationDisplayStatus(app)].icon}
                  />
                </Table.Cell>

                <Table.Cell>
                  <ApplicationActionsDropdown
                    application={app}
                    isLoading={mutations.isLoading.isLoadingAny}
                    onAction={actions.handleAction}
                    onDownloadProfile={downloads.onDownloadProfile}
                    isDownloading={downloads.isDownloading}
                    isOpen={openDropdownId === app.id}
                    onToggle={(open) => setOpenDropdownId(open ? app.id : null)}
                    direction={index === applications.length - 1 ? "top" : "bottom"}
                  />
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Table>
    </div>
  );
}
